import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  ReportPublishJobResponse,
} from '@ansvar-sakerhet/api-client';
import { Spinner } from '../../../../../../components/Spinner';
import { S } from './PublishReportModal.styles';

type StepStatus = 'pending' | 'active' | 'complete' | 'failed';

type Step = {
  id: string;
  label: string;
  status: StepStatus;
};

type Props = {
  open: boolean;
  isRunning: boolean;
  job: ReportPublishJobResponse | null;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
};

const getStepStatuses = (
  job: ReportPublishJobResponse | null,
  isRunning: boolean,
  error: string | null
): Step[] => {
  const status = job?.status;
  const hasFailed = Boolean(error) && !isRunning && status !== 'completed';

  const draftStatus: StepStatus = 'complete';

  let generateStatus: StepStatus = 'pending';
  if (!job && isRunning) {
    generateStatus = 'active';
  } else if (status === 'queued' || status === 'generating') {
    generateStatus = 'active';
  } else if (
    status === 'uploading' ||
    status === 'completed' ||
    status === 'failed'
  ) {
    generateStatus = 'complete';
  }

  let uploadStatus: StepStatus = 'pending';
  if (status === 'uploading') {
    uploadStatus = 'active';
  } else if (status === 'completed') {
    uploadStatus = 'complete';
  }

  let doneStatus: StepStatus = 'pending';
  if (status === 'completed') {
    doneStatus = 'complete';
  }

  if (hasFailed) {
    if (status === 'failed' || !job) {
      if (uploadStatus === 'active') {
        uploadStatus = 'failed';
      } else if (generateStatus === 'active' || generateStatus === 'complete') {
        generateStatus = 'failed';
      } else {
        generateStatus = 'failed';
      }
    } else if (generateStatus === 'active') {
      generateStatus = 'failed';
    } else if (uploadStatus === 'active') {
      uploadStatus = 'failed';
    }
  }

  return [
    { id: 'draft', label: 'Sparar utkast…', status: draftStatus },
    { id: 'generate', label: 'Genererar rapport…', status: generateStatus },
    {
      id: 'upload',
      label: 'Laddar upp till kundportalen…',
      status: uploadStatus,
    },
    { id: 'done', label: 'Klar', status: doneStatus },
  ];
};

const renderIndicator = (status: StepStatus) => {
  if (status === 'active') {
    return <Spinner />;
  }

  if (status === 'complete') {
    return '✓';
  }

  if (status === 'failed') {
    return '!';
  }

  return '·';
};

const isTerminalFailure = (
  job: ReportPublishJobResponse | null,
  error: string | null,
  isRunning: boolean
) => {
  if (isRunning) return false;
  if (job?.status === 'failed') return true;
  return Boolean(error);
};

export const PublishReportModal: React.FC<Props> = (props) => {
  const steps = getStepStatuses(props.job, props.isRunning, props.error);
  const failed = isTerminalFailure(props.job, props.error, props.isRunning);
  const succeeded = props.job?.status === 'completed' && !props.isRunning;

  if (!props.open) {
    return null;
  }

  return (
    <Dialog.Root
      open={props.open}
      onOpenChange={(open) => {
        if (!open && !props.isRunning) {
          props.onClose();
        }
      }}
    >
      <Dialog.Portal>
        <S.Overlay onPointerDown={(event) => event.preventDefault()}>
          <S.Content
            onEscapeKeyDown={(event) => {
              if (props.isRunning) event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              if (props.isRunning) event.preventDefault();
            }}
          >
            <S.ContentHeader>
              <Dialog.Title asChild>
                <S.H4>Publicerar rapport</S.H4>
              </Dialog.Title>
            </S.ContentHeader>

            <Dialog.Description asChild>
              <S.B2>
                Rapporten sparas och publiceras i kundportalen. Stäng inte
                fönstret förrän publiceringen är klar.
              </S.B2>
            </Dialog.Description>

            <S.StepList>
              {steps.map((step) => (
                <S.StepItem key={step.id} $status={step.status}>
                  <S.StepIndicator $status={step.status}>
                    {renderIndicator(step.status)}
                  </S.StepIndicator>
                  <span>{step.label}</span>
                </S.StepItem>
              ))}
            </S.StepList>

            {succeeded && (
              <S.SuccessMessage>
                Rapporten är publicerad i kundportalen
              </S.SuccessMessage>
            )}

            {failed && (
              <S.ErrorMessage>
                {props.error ??
                  props.job?.errorMessage ??
                  'Något gick fel vid publiceringen.'}
              </S.ErrorMessage>
            )}

            {!props.isRunning && (succeeded || failed) && (
              <S.ContentFooter>
                {failed && (
                  <S.Button $outlined $width={180} onClick={props.onRetry}>
                    Försök igen
                  </S.Button>
                )}
                <S.Button $width={180} onClick={props.onClose}>
                  Stäng
                </S.Button>
              </S.ContentFooter>
            )}
          </S.Content>
        </S.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
